import React, { useState } from 'react';
import { Env, ErrorBanner, Spinner, SuccessBanner } from '../../shared';
import { useKillSwitch } from '../api/useKillSwitch';
import { useTriggerSnapshot } from '../api/useTriggerSnapshot';

const MIN_REASON = 5;

/**
 * Kill Switch panel (SSA-818): flip the spin-wheel AppConfig flag, jump to the
 * economy-health dashboard (SSA-825), and manually re-run the leaderboard
 * snapshot (SSA-816). The spin backend is POST-only with no read, so the panel
 * asks the operator to choose the target state explicitly rather than showing a
 * toggle of unknown current value.
 */
export function KillSwitchPanel({ env, onOpenEconomy }: { env: Env; onOpenEconomy: () => void }) {
  const ks = useKillSwitch(env);
  const snap = useTriggerSnapshot(env);

  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<boolean | null>(null); // target state awaiting confirm
  const [notice, setNotice] = useState<string | null>(null);
  const [marketId, setMarketId] = useState('all');

  const applyKill = async () => {
    if (pending === null || reason.trim().length < MIN_REASON) return;
    const res = await ks.setSpinEnabled(pending, reason.trim());
    if (res) {
      setNotice(`Spin wheel ${res.enabled ? 'ENABLED' : 'DISABLED'} (config v${res.config_version}).`);
      setPending(null);
      setReason('');
    }
  };

  const runSnapshot = async () => {
    setNotice(null);
    const res = await snap.trigger(marketId.trim() || 'all');
    if (res) setNotice('Leaderboard snapshot queued (recomputes all active markets).');
  };

  return (
    <div className="space-y-5">
      {notice && <SuccessBanner message={notice} />}

      {/* Spin wheel kill switch */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white">Spin-wheel kill switch</h3>
        <p className="text-xs text-gray-500 mt-1">
          Flips the <code className="text-gray-400">gamification_spin_wheel</code> AppConfig flag for{' '}
          <span className="uppercase font-medium">{env}</span>. Takes effect within a minute.
        </p>

        {ks.error && (
          <div className="mt-3">
            <ErrorBanner message={ks.error} />
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button
            onClick={() => setPending(false)}
            className={`px-3 py-2 text-xs font-medium rounded-lg border ${
              pending === false
                ? 'bg-red-600 border-red-500 text-white'
                : 'bg-red-950/40 border-red-800/50 text-red-300 hover:bg-red-900/40'
            }`}
          >
            Disable spin
          </button>
          <button
            onClick={() => setPending(true)}
            className={`px-3 py-2 text-xs font-medium rounded-lg border ${
              pending === true
                ? 'bg-emerald-600 border-emerald-500 text-white'
                : 'bg-emerald-950/40 border-emerald-800/50 text-emerald-300 hover:bg-emerald-900/40'
            }`}
          >
            Enable spin
          </button>
        </div>

        {pending !== null && (
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`Reason (min ${MIN_REASON} chars)`}
              className="flex-1 px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
            />
            <button
              onClick={applyKill}
              disabled={ks.loading || reason.trim().length < MIN_REASON}
              className="px-4 py-2 text-xs font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {ks.loading && <Spinner size="sm" />}
              Confirm {pending ? 'enable' : 'disable'}
            </button>
          </div>
        )}
      </div>

      {/* Economy dashboard jump */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-white">Economy health</h3>
          <p className="text-xs text-gray-500 mt-1">Liability, mint/burn, redemptions, fraud signals, spin distribution.</p>
        </div>
        <button
          onClick={onOpenEconomy}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500"
        >
          Open dashboard →
        </button>
      </div>

      {/* Manual snapshot */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white">Re-run leaderboard snapshot</h3>
        <p className="text-xs text-gray-500 mt-1">
          Queues the weekly snapshot job now — useful after a fraud cleanup. Currently recomputes{' '}
          <span className="text-gray-400">all active markets</span> regardless of the id entered.
        </p>
        {snap.error && (
          <div className="mt-3">
            <ErrorBanner message={snap.error} />
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <input
            value={marketId}
            onChange={(e) => setMarketId(e.target.value)}
            placeholder="market id (or 'all')"
            className="flex-1 px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
          />
          <button
            onClick={runSnapshot}
            disabled={snap.loading}
            className="px-4 py-2 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700 disabled:opacity-40 flex items-center gap-2"
          >
            {snap.loading && <Spinner size="sm" />}
            Trigger
          </button>
        </div>
      </div>
    </div>
  );
}
